import { useCallback, useSyncExternalStore } from 'react';

import { graphql, useFragment, useMutation } from 'react-relay';

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';
import { buildNavigationAriForSidebarData } from '@atlassian/navigation-common/navigation-ari';
import {
	DEFAULT_COLLAPSED_KEY,
	getSideNavDefaultCollapsedLocalStorageKey,
} from '@atlassian/navigation-common/side-nav-storage';
import type { NetworkStorageParams } from '@atlassian/navigation-common/types';

import type { UseSideNavToggleStateResult } from '../common/types';

import type { useSideNavToggleStateMutation } from './__generated__/useSideNavToggleStateMutation.graphql';
import type { useSideNavToggleStateQuery$key } from './__generated__/useSideNavToggleStateQuery.graphql';

/**
 * The `getServerSnapshot` for the local store.
 *
 * `localStorage` does not exist on the server, so the server (and the hydration
 * pass, which React also renders with this snapshot) always sees the expanded
 * default. Declared at module scope so the reference is stable across renders.
 */
function getServerSnapshot(): boolean {
	return false;
}

/**
 * Local-storage store. Reads/writes `localStorage` and executes no GraphQL
 * hooks.
 *
 * This is one of two sibling store hooks (`useLocalToggleState` /
 * `useNetworkToggleState`). Each is called *unconditionally* by its own
 * dedicated component — the branch between them happens at the component
 * boundary in `GlobalTopNavigation`, mirroring the way GSN picks between
 * `<LocalStore>` and `<NetworkStore>`. Never call both from one component.
 *
 * When no preference has been stored, the side nav defaults to expanded, so
 * `defaultCollapsed` is `false`.
 *
 * ## SSR
 *
 * `localStorage` is a browser-only mutable source, so it is read through
 * `useSyncExternalStore` — the React-blessed primitive for exactly this. Here it
 * is used for exactly two things the previous ad-hoc read did not give us:
 *
 *  1. A `getServerSnapshot`, so SSR renders the expanded default instead of
 *     diverging — see the `ensure-use-sync-external-store-server-snapshot`
 *     platform lint rule.
 *  2. A hydration pass that matches the server HTML, because React renders it
 *     with the server snapshot too.
 *
 * NOTE: `subscribe` is DELIBERATELY a no-op, so this store never pushes updates
 * — please do not "fix" it. `defaultCollapsed` is only an *initial* value: once
 * `SideNavToggleButton` has mounted, ADS owns the visible state, so re-rendering
 * this hook when the stored value changes would achieve nothing. Wiring up the
 * `storage` event would add cross-tab churn for no visible effect. If a future
 * requirement genuinely needs live sync, the ADS side must be able to accept a
 * *controlled* collapsed value first.
 *
 * `readStateFromLocalStorage` reads a stringified boolean from GTN's dedicated
 * `-default-collapsed` key, so `getSnapshot` stays cheap even though React calls
 * it on every render.
 *
 * KNOWN LIMITATION: the real stored value therefore only arrives on the render
 * immediately *after* hydration. ADS snapshots `defaultCollapsed` into
 * `useState` when `SideNavToggleButton` mounts and ignores later changes, so on
 * an SSR'd page load the button's *initial icon* reflects the expanded default
 * rather than the stored preference. This self-corrects in practice: whenever a
 * `<SideNav>` is present, ADS reads the visibility from context instead of this
 * prop (`sideNavState === null ? !defaultCollapsed : isSideNavExpandedOnDesktop`).
 * Forcing a remount to work around it was considered and rejected — it perturbs
 * ADS's own visibility state machine for no real-world gain.
 *
 * @see platform/packages/design-system/navigation-system/src/ui/page-layout/side-nav/toggle-button.tsx
 *
 * @param app - the app identifier used to scope the localStorage key. GTN writes
 * to its own `-default-collapsed` key rather than GSN's shared blob; the two are
 * intentionally separate until a later work package aligns them.
 */
export function useLocalToggleState(app: string): UseSideNavToggleStateResult {
	// Intentionally never notifies — see the "NOTE" in this hook's docstring
	// before changing this. `useSyncExternalStore` is used here purely for its
	// SSR/hydration behaviour via `getServerSnapshot`.
	const subscribe = useCallback((_onStoreChange: () => void) => () => {}, []);

	// Returns a primitive, so React's referential-equality check is a value
	// comparison and cannot loop.
	const getSnapshot = useCallback(() => readStateFromLocalStorage(app) ?? false, [app]);

	const defaultCollapsed = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

	const setCollapsed = useCallback(
		(collapsed: boolean) => {
			writeStateToLocalStorage(app, collapsed);
		},
		[app],
	);

	return { defaultCollapsed, setCollapsed };
}

/**
 * Network store. Reads the `navPlatSidebarData` fragment and writes changes via
 * the `settings_updateNavigationCustomisation` mutation.
 *
 * See `useLocalToggleState` for why this must be called unconditionally by a
 * dedicated component rather than selected at runtime inside a shared hook.
 */
export function useNetworkToggleState(
	app: string,
	networkStorageParams: NetworkStorageParams | undefined,
	queryRef: useSideNavToggleStateQuery$key | null | undefined,
): UseSideNavToggleStateResult {
	// The `key`/`value` fields are consumed via `readStateFromGQL` rather than
	// inline, and the fragment is colocated in this non-component hook — matching
	// the pattern used by GSN's controllers.
	/* eslint-disable @atlassian/relay/unused-fields */
	/* eslint-disable @atlassian/relay/graphql-naming */
	// NOTE: the `first: 100` page size below MUST stay in sync with the page size
	// GlobalSideNavigation uses when reading `properties` on this same record (see
	// `useNavSettingsService` in @atlassian/global-side-navigation). Both components
	// read the SAME record, so a smaller page size here could miss a property the
	// side nav wrote. GlobalRoot also composes both fragments into a single query,
	// and Relay rejects the merge outright if the arguments differ.
	//
	// It cannot be extracted to a shared constant: GraphQL literals can't reference
	// TS values, and a shared Relay provider is impossible because Relay identifies
	// providers by their relative path string, which necessarily differs between
	// these two packages.
	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const data = useFragment<useSideNavToggleStateQuery$key>(
		graphql`
			fragment useSideNavToggleStateQuery on Query {
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) @skip(if: $skipNavPlatSidebarData) {
					properties(first: 100) {
						nodes {
							key
							value
						}
					}
				}
			}
		`,
		queryRef ?? null,
	);
	/* eslint-enable @atlassian/relay/unused-fields */
	/* eslint-enable @atlassian/relay/graphql-naming */

	const defaultCollapsed = readStateFromGQL(data?.navPlatSidebarData?.properties?.nodes) ?? false;

	const [commitMutation] = useMutation<useSideNavToggleStateMutation>(graphql`
		mutation useSideNavToggleStateMutation(
			$entityAri: ID!
			$ownerAri: ID
			$properties: [SettingsDisplayPropertyInput]
		) {
			settings_updateNavigationCustomisation(
				input: { entityAri: $entityAri, ownerAri: $ownerAri, properties: $properties }
			) {
				properties(first: 20) {
					nodes {
						key
						value
					}
				}
			}
		}
	`);

	// Depend on the primitive fields rather than the `networkStorageParams`
	// object. Consumers almost always pass an inline object literal (as the
	// package's own example does), which would give this callback a new identity
	// on every render and quietly defeat any memoisation downstream of it.
	const activationId = networkStorageParams?.activationId;
	const ownerAri = networkStorageParams?.ownerAri;
	const siteId = networkStorageParams?.siteId;

	const setCollapsed = useCallback(
		(collapsed: boolean) => {
			if (!activationId || !siteId) {
				// Without these we cannot derive the ARI of the record to write to.
				// Fail quietly rather than throwing on a user interaction.
				return;
			}

			commitMutation({
				variables: {
					// Derived — never passed in — so that the record we write to is
					// always the same one the query read from. See the module-level
					// note on `buildNavigationAriForSidebarData`.
					entityAri: buildNavigationAriForSidebarData({
						activationId,
						firstPartyApp: app,
						siteId,
					}),
					ownerAri,
					properties: [{ key: DEFAULT_COLLAPSED_KEY, value: String(collapsed) }],
				},
			});
		},
		[activationId, app, commitMutation, ownerAri, siteId],
	);

	return { defaultCollapsed, setCollapsed };
}

// ---------------------------------------------------------------------------
// Storage helpers (exported for unit testing).
// ---------------------------------------------------------------------------

/**
 * Reads the written `defaultCollapsed` value from `localStorage`.
 *
 * Reads GTN's dedicated `-default-collapsed` key, whose value is a stringified
 * boolean — so no JSON parsing (and therefore no parse-memoisation) is needed,
 * even though `useSyncExternalStore` calls this on every render.
 *
 * @param app - the app identifier used to scope the key (see
 * `getSideNavDefaultCollapsedLocalStorageKey`).
 *
 * @returns the stored collapsed boolean, or `undefined` when there is no stored
 * preference (or when running server-side).
 */
export function readStateFromLocalStorage(app: string): boolean | undefined {
	// Guard against SSR — localStorage is not available on the server.
	if (typeof window === 'undefined') {
		return undefined;
	}

	// `AtlBrowserStorageLocal.getItem` is synchronous and SSR-safe (returns
	// `null` when web storage is unavailable), so it is a direct drop-in for
	// `localStorage.getItem` here.
	const raw = AtlBrowserStorageLocal.getItem(getSideNavDefaultCollapsedLocalStorageKey(app));

	// The value is a stringified boolean written by `writeStateToLocalStorage`.
	// Anything else (missing key, or a legacy/unexpected value) means "no stored
	// preference", so we fall back to the expanded default upstream.
	if (raw === 'true') {
		return true;
	}
	if (raw === 'false') {
		return false;
	}
	return undefined;
}

/**
 * Writes the collapsed state to `localStorage`, merging with any existing
 * state so we don't clobber other data GSN stores under the same key.
 *
 * @param app - the app identifier used to scope the key (see `getSideNavStateLocalStorageKey`).
 */
export function writeStateToLocalStorage(app: string, collapsed: boolean): void {
	// Guard against SSR — localStorage is not available on the server.
	if (typeof window === 'undefined') {
		return;
	}

	const key = getSideNavDefaultCollapsedLocalStorageKey(app);

	// Store ONLY the collapsed boolean (as a string) under GTN's dedicated
	// `-default-collapsed` key. This is intentionally NOT GSN's richer JSON blob —
	// see `getSideNavDefaultCollapsedLocalStorageKey`. GSN will be migrated to read
	// from this key in a later work package.
	//
	// `AtlBrowserStorageLocal.setItem` is async (it consults the storage-controls
	// consent state) and this write is fire-and-forget — we do not block the user
	// interaction on it. `Promise.resolve` guards against a synchronous throw, and
	// the `.catch` swallows the rejection so a failed write can never surface as an
	// unhandled promise rejection.
	Promise.resolve(AtlBrowserStorageLocal.setItem(key, String(collapsed))).catch((error) => {
		// eslint-disable-next-line no-console
		console.warn('Failed to write side nav state to localStorage:', error);
	});
}

/**
 * Given the `properties.nodes` list from the network navigation-customization
 * fragment, extracts the `defaultCollapsed` value and parses it into a boolean.
 *
 * The value is stored as the string `'true'` / `'false'`, mirroring the read
 * logic used by GlobalSideNavigation (GSN).
 *
 * @returns the parsed collapsed boolean, or `undefined` when the property is
 * absent.
 */
export function readStateFromGQL(
	nodes:
		| ReadonlyArray<{ readonly key: string | null; readonly value: string | null } | null>
		| null
		| undefined,
): boolean | undefined {
	const value = (nodes ?? []).find((node) => node?.key === DEFAULT_COLLAPSED_KEY)?.value;

	if (value === 'true') {
		return true;
	} else if (value === 'false') {
		return false;
	}

	return undefined;
}
